// One normalized shape for a product, so the product modal can be a single presentational
// component instead of a V1 copy and a V2 copy that drift apart.
//
// V1 gets its data from Vuex as JSON:API resources (`x.attributes.*`, money as `{cents, format}`);
// V2 gets a flat snapshot from the V2 store endpoint (`x.*`, money as `*_cents` plus a separate
// `*_formatted`). Everything below that difference is identical, so it is normalized once here and
// the modal never learns which checkout version it is rendering in.
//
// These functions are pure. Callers resolve their own store/relationship lookups first and pass
// plain values in - `fromV1` must not reach into Vuex, or it stops being testable.

// A modifier group's selection behavior, normalized away from the backend's `quantity_option`.
export const SELECT_ONE = 'select_one'
export const SELECT_MANY = 'select_many'
export const QUANTITY = 'quantity'

const QUANTITY_OPTION_TO_MODE = {
  choose_one: SELECT_ONE,
  choose_many: SELECT_MANY,
  custom_quantities: QUANTITY
}

// `variants.maximum` defaults to 100 in the schema (db/schema.rb:5082), so almost every row carries
// it without a merchant having chosen it; 0 is the other way of saying "unset". Both mean "no
// merchant-configured cap", so they collapse to null and the modal applies the cap implied by the
// mode instead. The cost is that a merchant wanting a cap of exactly 100 cannot have one - the same
// trade-off V1 has always made.
const SENTINEL_MAX = 100

// `variants.quantity_option` is a Rails enum (app/models/variant.rb:10) stored as an ordinal.
// Both serializers normally map it to its name via `to_enum_string`, but that call is `&.`-guarded,
// so a NULL column arrives as null - and any path that skips the mapping delivers the raw ordinal
// or its string form. Falling back to SELECT_MANY for all of those silently turned single-choice
// groups into multi-select, which let customers tick several options under "Please choose 1".
const ENUM_ORDINAL_TO_MODE = [SELECT_ONE, SELECT_MANY, QUANTITY]

function toMode(quantityOption, max) {
  const named = QUANTITY_OPTION_TO_MODE[quantityOption]
  if (named) return named

  if (quantityOption != null && /^\d+$/.test(String(quantityOption))) {
    const ordinal = ENUM_ORDINAL_TO_MODE[Number(quantityOption)]
    if (ordinal) return ordinal
  }

  // Last resort: a group that can hold at most one option is single-choice whatever the enum says.
  if (Number(max) === 1) return SELECT_ONE
  return SELECT_MANY
}

function normalizeMin(value) {
  const min = Number(value) || 0
  return min > 0 ? min : 0
}

function normalizeMax(value, { sentinel } = {}) {
  const max = Number(value) || 0
  if (max <= 0) return null
  if (sentinel != null && max === sentinel) return null
  return max
}

// `required` is authoritative when the backend sends it, except on a quantity group with a minimum,
// which both carts reject below that minimum whatever the flag says. V1's serializer historically
// did not send the flag, and its only notion of a mandatory group is a minimum above zero, so fall
// back to that.
function isRequired(explicit, min, mode) {
  if (mode === QUANTITY && min > 0) return true
  if (explicit != null) return Boolean(explicit)
  return min > 0
}

function byOrder(a, b) {
  return (a.order || 0) - (b.order || 0)
}

export function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

// The deep-link slug is derived, not stored: there is no slug column on Product. The trailing
// cpsp id is what actually resolves the product, so a merchant renaming a product changes the
// pretty part of the URL without breaking existing links.
export function productSlug(name, cpspId) {
  const base = slugify(name)
  if (cpspId == null) return base
  return base ? `${base}-${cpspId}` : String(cpspId)
}

export function cpspIdFromSlug(slug) {
  const match = /-(\d+)$/.exec(String(slug || ''))
  if (match) return Number(match[1])
  return /^\d+$/.test(String(slug || '')) ? Number(slug) : null
}

// The five static segments that sit beside the Selection route under /:storeSlug?/b/:bottleid.
// vue-router ranks a static segment above a dynamic one, so a product whose bare slug equalled one
// of these would send the customer to that page instead of opening the product. No product in
// production slugifies to any of them today - the index still refuses to mint one, because the
// alternative is a silent wrong-page navigation the moment a merchant names something "Receipt".
export const RESERVED_PRODUCT_SLUGS = ['cart', 'checkout', 'login', 'notice', 'receipt']

function compareIds(a, b) {
  if (a === b) return 0
  if (a == null) return 1
  if (b == null) return -1
  const difference = Number(a) - Number(b)
  if (Number.isFinite(difference)) return difference === 0 ? 0 : difference < 0 ? -1 : 1
  return String(a) < String(b) ? -1 : 1
}

// Ordered best-first. Only the owner of a name gets the bare slug, and only when that slug is
// actually usable; everything else falls back to the fully-qualified form, then to the bare id.
function slugCandidates(base, cpspId, isOwner) {
  const qualified = base ? `${base}-${cpspId}` : String(cpspId)
  if (!isOwner || !base || RESERVED_PRODUCT_SLUGS.includes(base)) return [qualified, String(cpspId)]
  return [base, qualified, String(cpspId)]
}

/**
 * A store-scoped map between cpsp ids and URL slugs.
 *
 * The point is that most products can have a clean `/hot-chicken-bowl` URL, and only the ones that
 * are genuinely ambiguous have to carry an id. Production bears that out: of 18,992 products, 100
 * (store, slug) pairs collide - and 95 of those are whitespace variants of one name.
 *
 * Ownership is deterministic rather than first-come, and that is the whole trick. The lowest
 * product id owns the bare slug, so a merchant adding a second "Gift Card" next month does not
 * silently move the URL of the one already shared - the newcomer takes the qualified form instead.
 * Within a single product listed in several categories, the catalogue listing beats an upsell
 * listing: a deep link should open the offer the menu shows, not the upsell price.
 *
 * @param entries [{ cpspId, productId, name, isUpsell }] - every pair on the store's menu
 */
export function buildProductSlugIndex(entries = []) {
  const normalized = (entries || [])
    .filter((entry) => entry && entry.cpspId != null)
    .map((entry) => ({
      cpspId: String(entry.cpspId),
      productId: entry.productId == null ? null : String(entry.productId),
      base: slugify(entry.name),
      isUpsell: Boolean(entry.isUpsell)
    }))

  normalized.sort((a, b) => {
    if (a.base !== b.base) return a.base < b.base ? -1 : 1
    const byProduct = compareIds(a.productId, b.productId)
    if (byProduct !== 0) return byProduct
    if (a.isUpsell !== b.isUpsell) return a.isUpsell ? 1 : -1
    return compareIds(a.cpspId, b.cpspId)
  })

  const slugByCpspId = new Map()
  const cpspIdBySlug = new Map()
  let previousBase = null

  for (const entry of normalized) {
    if (slugByCpspId.has(entry.cpspId)) continue
    const isOwner = entry.base !== previousBase
    previousBase = entry.base

    // A qualified slug can in principle collide with another product literally named "Hot Chic
    // 48213", so take the first candidate nobody has claimed rather than assuming.
    const slug = slugCandidates(entry.base, entry.cpspId, isOwner).find((candidate) => !cpspIdBySlug.has(candidate))
    if (!slug) continue

    slugByCpspId.set(entry.cpspId, slug)
    cpspIdBySlug.set(slug, entry.cpspId)
  }

  return { slugByCpspId, cpspIdBySlug }
}

export function slugForCpspId(index, cpspId) {
  if (!index || cpspId == null) return null
  return index.slugByCpspId.get(String(cpspId)) || null
}

// Resolution is by slug string, not by parsing an id out of it - 491 products in production have
// names ending in digits ("Kit #1", "Beef, Ground 80/20"), and parsing those would invent a cpsp id
// out of the name. The parse survives only as a fallback, and only when it names a pair that is
// actually on this menu, so a fully-qualified link keeps working for a product that owns the bare
// slug and so does any link minted before this index existed.
export function cpspIdForSlug(index, slug) {
  if (!index || slug == null || slug === '') return null

  const exact = index.cpspIdBySlug.get(String(slug))
  if (exact != null) return exact

  const parsed = cpspIdFromSlug(slug)
  if (parsed != null && index.slugByCpspId.has(String(parsed))) return String(parsed)
  return null
}

// Descriptions are pasted in from other tools and often end in a run of blank lines. The modal
// keeps line breaks on purpose, so left alone those render as empty space between the copy and
// the option groups - on a phone the sizes end up below the fold. A single blank line still
// separates paragraphs; anything beyond that goes.
function cleanDescription(value) {
  if (value == null) return ''
  return String(value)
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function buildGroup({ id, name, description, required, mode, min, max, isVisible, options }) {
  return {
    id,
    name: name || '',
    description: cleanDescription(description) || null,
    required,
    mode,
    min,
    max,
    limit: max != null ? max : mode === QUANTITY ? SENTINEL_MAX : null,
    isVisible,
    options
  }
}

// Offered means visible and with something to pick. A group emptied of options asks a question
// with no answers: the backend has nothing to fill and lets the line through, so showing it only
// left a required one blocking "Add to cart" for good (BOT-1753).
function isOffered(group) {
  return group.isVisible && group.options.length > 0
}

function buildOption({
  id,
  name,
  description,
  deltaCents,
  inventoryRemaining,
  soldOut,
  autofillPreferenceOverride = null,
  nutrition = null
}) {
  return {
    id,
    name: name || '',
    description: cleanDescription(description) || null,
    deltaCents: Number(deltaCents) || 0,
    inventoryRemaining: inventoryRemaining == null ? null : Number(inventoryRemaining),
    soldOut: Boolean(soldOut),
    // The name a package's variant preference has to match, when the merchant set one. Not shown
    // anywhere; it exists so the modal picks the option the backend would have picked.
    autofillPreferenceOverride: autofillPreferenceOverride || null,
    nutrition: nutrition || null
  }
}

/**
 * The most this line may be. Two independent caps, and the customer is held to the tighter:
 *
 * - remaining inventory, plus whatever this line already holds (that quantity is already counted
 *   against the remaining figure, so it has to be given back before comparing)
 * - the merchant's `max_per_order`, which the backend enforces as a hard cart-item validation
 *   (CategoryProductStoreCartItem#less_than_or_equal_to_max_per_order). 0 means "no cap".
 *
 * The two versions used to read one each - V1 only inventory, V2 only max_per_order, and V2's
 * defaults to 0 so it was in practice uncapped - which meant one could oversell and the other
 * could build a line the POST would reject.
 */
export function quantityCap({ inventoryRemaining = null, merchantMax = null, cartQuantity = 0 } = {}) {
  const caps = []
  if (inventoryRemaining != null) caps.push(Number(inventoryRemaining) + (Number(cartQuantity) || 0))
  if (Number(merchantMax) > 0) caps.push(Number(merchantMax))
  if (!caps.length) return null
  return Math.max(Math.min(...caps), 0)
}

/**
 * The quantity the cart will actually hold for a requested one.
 *
 * The backend does not take the number it is given: it floors it at the product's starting
 * quantity and then rounds it UP to the next multiple of the step - V1 in
 * CategoryProductStoreCartItem#quantity_minimum / #quantity_rounded_up_to_nearest_step, V2 in
 * CartPricingService#clamp_quantity_to_inventory, which mirrors it deliberately. So a stepper that
 * lets a customer sit on 4 of something sold in 3s is quoting a total for a quantity that cannot
 * exist; the cart comes back holding 6.
 *
 * Zero is exempt. It is not a quantity, it is a removal, and the backend guards it the same way
 * (BOT-970: a sold-out line must stay at 0 rather than be re-inflated to the floor).
 */
export function snapQuantity(value, { step = 1, floor = 0 } = {}) {
  const requested = Math.trunc(Number(value) || 0)
  if (requested <= 0) return 0

  const smallest = Math.max(0, Math.trunc(Number(floor) || 0))
  const size = Math.max(1, Math.trunc(Number(step) || 0))
  const floored = Math.max(requested, smallest)

  if (size <= 1 || floored % size === 0) return floored
  return Math.ceil(floored / size) * size
}

function buildViewModel(fields) {
  const vm = {
    slug: productSlug(fields.name, fields.cpspId),
    ...fields,
    description: cleanDescription(fields.description)
  }
  vm.heroUrl = vm.imageUrls[0] || null
  vm.thumbUrl = vm.thumbUrl || vm.heroUrl
  vm.hasModifiers = vm.modifierGroups.length > 0
  // "from $X" only makes sense when a choice can push the price up. A product with no priced
  // option always costs exactly its base price, which is why the prototype renders a bare
  // "$16.50" on the drink and "from $23.00" on the salad.
  vm.hasPricedOption = vm.modifierGroups.some((group) => group.options.some((option) => option.deltaCents > 0))
  return vm
}

// ---------------------------------------------------------------------------
// V1 - JSON:API resources resolved out of Vuex by the caller
// ---------------------------------------------------------------------------

export function fromV1({
  product,
  categoryProductStorePair,
  variants = [],
  labels = [],
  attachments = [],
  basePriceCents = 0,
  showPrices = true,
  cartItem = null
} = {}) {
  const productAttrs = (product && product.attributes) || {}
  const cpspAttrs = (categoryProductStorePair && categoryProductStorePair.attributes) || {}

  const modifierGroups = [...variants]
    .map((variant) => {
      const attrs = variant.attributes || {}
      const min = normalizeMin(attrs.minimum)
      const options = [...(variant.variantOptions || attrs.variant_options || [])]
        .map((option) => {
          const optionAttrs = option.attributes || {}
          const hasLimit = Boolean(optionAttrs.has_quantity_limit)
          const remaining = hasLimit ? optionAttrs.inventory_remaining : null
          return buildOption({
            id: option.id,
            name: optionAttrs.name,
            description: optionAttrs.description,
            deltaCents: (optionAttrs.price && optionAttrs.price.cents) || 0,
            inventoryRemaining: remaining,
            soldOut: hasLimit && optionAttrs.inventory_remaining === 0,
            autofillPreferenceOverride: optionAttrs.autofill_preference_override
          })
        })
        .sort(byOrder)

      const max = normalizeMax(attrs.maximum, { sentinel: SENTINEL_MAX })
      const mode = toMode(attrs.quantity_option, max)

      return buildGroup({
        id: attrs.id != null ? attrs.id : variant.id,
        name: attrs.name,
        description: attrs.description,
        required: isRequired(attrs.required, min, mode),
        mode,
        min,
        max,
        // V1 has always hidden a group flagged `is_visible: false`. Absent means visible.
        isVisible: attrs.is_visible !== false,
        options
      })
    })
    .filter(isOffered)

  return buildViewModel({
    id: product && product.id,
    cpspId: categoryProductStorePair && categoryProductStorePair.id,
    frontendKey: cartItem && cartItem.frontend_key ? cartItem.frontend_key : null,
    name: productAttrs.name || '',
    description: productAttrs.description || '',
    // V1 chips read `label_product_text` ("Contains Dairy"), not the bare label name.
    chips: labels
      .map((label) => (label.attributes && label.attributes.label_product_text) || '')
      .filter(Boolean)
      .map((text) => ({ text })),
    imageUrls: attachmentUrls(attachments, attachmentUrlV1, { highRes: true }),
    thumbUrl: attachmentUrls(attachments, attachmentUrlV1)[0] || null,
    modifierGroups,
    basePriceCents: Number(basePriceCents) || 0,
    showPrices: Boolean(showPrices) && cpspAttrs.show_prices !== false,
    // Both live on the PRODUCT in V1 (ProductSerializer exposes `step` and `starting_quantity`),
    // not on the pair - the pair carries no quantity rules at all.
    quantityStep: Number(productAttrs.step) || 1,
    startingQuantity: Number(productAttrs.starting_quantity) || 0,
    quantityUnit: productAttrs.unique_quantity_name_single || null,
    quantityUnitPlural: productAttrs.unique_quantity_name_plural || productAttrs.unique_quantity_name_single || null,
    // Already resolved by buildV1ProductViewModel, which is where the cpsp's quantity_remaining
    // and the product's max_per_order both live.
    maxPerOrder: Number(cpspAttrs.max_per_order) || null,
    nutrition: productAttrs.nutritional_information || null
  })
}

function attachmentUrls(attachments, toUrl, options = {}) {
  return (attachments || []).map((attachment) => toUrl(attachment, options)).filter(Boolean)
}

function attachmentUrlV1(attachment, { highRes = false } = {}) {
  const sources = attachment.sources || (attachment.attributes && attachment.attributes.sources) || []
  const attributes = (sources[0] && sources[0].attributes) || {}
  return (highRes && attributes.high_res_url) || attributes.secure_url || null
}

// ---------------------------------------------------------------------------
// V2 - flat store-snapshot product
// ---------------------------------------------------------------------------

export function fromV2({ product, basePriceCents = null, cartItem = null } = {}) {
  const source = product || {}

  const modifierGroups = [...(source.variants || [])]
    .map((variant) => {
      const min = normalizeMin(variant.min)
      const options = [...(variant.variant_options || [])]
        .map((option) =>
          buildOption({
            id: option.id,
            name: option.name,
            description: option.description,
            deltaCents: option.price_cents,
            inventoryRemaining: option.inventory_remaining,
            soldOut: option.inventory_remaining === 0,
            autofillPreferenceOverride: option.autofill_preference_override,
            nutrition: option.nutritional_information
          })
        )
        .sort(byOrder)

      // Same sentinel as V1. `variants.maximum` defaults to 100 in the schema, so almost every row
      // carries it without a merchant ever choosing it - and V2 collapsing it was the difference
      // between a group heading that reads "Choose at least 1" and one that reads "Choose between
      // 1 and 100".
      const max = normalizeMax(variant.max, { sentinel: SENTINEL_MAX })
      const mode = toMode(variant.quantity_option, max)

      return buildGroup({
        id: variant.id,
        name: variant.name,
        description: variant.description,
        required: isRequired(variant.required, min, mode),
        mode,
        min,
        max,
        // Same rule as V1: a group flagged `is_visible: false` is hidden. Absent means visible, so
        // a snapshot cached before `build_variant` started emitting the field still renders.
        isVisible: variant.is_visible !== false,
        options
      })
    })
    .filter(isOffered)

  return buildViewModel({
    id: source.id,
    cpspId: source.cpsp_id,
    frontendKey: cartItem && cartItem.frontend_key ? cartItem.frontend_key : null,
    name: source.name || '',
    description: source.description || '',
    // The V2 snapshot sends each label as the same string V1 puts on a chip ("Contains Dairy") -
    // see StoreSnapshotService#build_product. Objects are still handled so an older cached
    // snapshot, or a future richer shape, both render.
    chips: (source.labels || [])
      .map((label) => (typeof label === 'string' ? label : label && (label.product_text || label.name)))
      .filter(Boolean)
      .map((text) => ({ text })),
    imageUrls: attachmentUrls(source.attachments, attachmentUrlV2, { highRes: true }),
    thumbUrl: attachmentUrls(source.attachments, attachmentUrlV2)[0] || null,
    modifierGroups,
    basePriceCents: Number(basePriceCents == null ? source.price_cents : basePriceCents) || 0,
    showPrices: source.show_prices !== false,
    quantityStep: Number(source.quantity_step) || 1,
    startingQuantity: Number(source.starting_quantity) || 0,
    quantityUnit: source.quantity_name || null,
    quantityUnitPlural: source.quantity_name || null,
    maxPerOrder: v2QuantityCap(source, cartItem),
    nutrition: source.nutritional_information || null
  })
}

/**
 * The most a V2 line may be.
 *
 * PRODUCT inventory only. `store_inventory_remaining` is the date-aware FulfillmentAvailability cap,
 * and V1's store page never applies it - its serializer is handed the store without the bottle, so
 * the date branch is never reached - so clamping on it here made V2 stricter than V1 on the same
 * screen (merchant-vue 24924c1). The server still clamps on submit, which is the same protection
 * model V1 has always run on.
 *
 * The figure comes from the cart item when there is one: the V2 cart POST response carries fresh
 * per-item inventory (BOT-859), which is how a limit changed elsewhere takes effect on the next
 * cart action rather than on the next page load. The cached snapshot is the fallback for a product
 * with no line yet.
 *
 * No cart credit, unlike V1. The two backends mean different things by "remaining": V1's
 * `quantity_remaining` comes off the live bottle payload with this cart already deducted, so the
 * line's own quantity has to be given back before comparing; V2 reports the stock available to the
 * line, deducting nothing. Crediting it would offer a customer holding all 3 of something a cap of
 * 6 - which is what the IN-A parity spec catches.
 */
function v2QuantityCap(source, cartItem) {
  const line = cartItem || {}
  const remaining =
    line.product_inventory_remaining != null ? line.product_inventory_remaining : source.product_inventory_remaining

  return quantityCap({
    inventoryRemaining: remaining == null ? null : Number(remaining),
    merchantMax: source.max_per_order
  })
}

function attachmentUrlV2(attachment, { highRes = false } = {}) {
  const sources = attachment.sources || []
  return (
    (highRes && attachment.high_res_url) ||
    (sources[0] && (sources[0].secure_url || (sources[0].attributes && sources[0].attributes.secure_url))) ||
    attachment.secure_url ||
    attachment.url ||
    null
  )
}

// ---------------------------------------------------------------------------
// Pricing - shared by both versions so the modal's running total cannot drift
// ---------------------------------------------------------------------------

// `selection` is `{ [groupId]: { [optionId]: quantity } }`.
export function selectedOptionsTotalCents(viewModel, selection = {}) {
  let total = 0
  for (const group of viewModel.modifierGroups || []) {
    const chosen = selection[group.id] || {}
    for (const option of group.options) {
      const quantity = Number(chosen[option.id]) || 0
      if (quantity > 0) total += option.deltaCents * quantity
    }
  }
  return total
}

// One unit, with its modifiers. This is what a radio row shows as an absolute price.
export function unitPriceCents(viewModel, selection = {}) {
  return (viewModel.basePriceCents || 0) + selectedOptionsTotalCents(viewModel, selection)
}

// The sticky footer's live total.
export function runningTotalCents(viewModel, selection = {}, quantity = 1) {
  const units = Math.max(0, Number(quantity) || 0)
  return unitPriceCents(viewModel, selection) * units
}

const NUTRITION_KEYS = [
  'serving_size',
  'servings_per_container',
  'calories',
  'total_fat',
  'saturated_fat',
  'trans_fat',
  'cholesterol',
  'sodium',
  'total_carbs',
  'dietary_fiber',
  'sugars',
  'added_sugar',
  'iron',
  'vitamin_d',
  'vitamin_c',
  'protein'
]

export function combinedNutrition(viewModel, selection = {}) {
  const sources = [viewModel?.nutrition]
  for (const group of viewModel?.modifierGroups || []) {
    const chosen = selection[group.id] || {}
    for (const option of group.options) {
      if (Number(chosen[option.id]) > 0) sources.push(option.nutrition)
    }
  }

  const present = sources.filter(Boolean)
  if (!present.length) return null

  return Object.fromEntries(
    NUTRITION_KEYS.map((key) => [key, present.reduce((sum, source) => sum + (Number(source[key]) || 0), 0)])
  )
}

// The card's "from $X". Null when there is nothing to add, so the card renders a plain price.
export function fromPriceCents(viewModel) {
  if (!viewModel.hasPricedOption) return null
  return viewModel.basePriceCents || 0
}

/**
 * What one cart line costs PER UNIT, so a customer reviewing their cart can read it off the card.
 *
 * Per unit, not extended: both checkouts have always priced a card this way - V1's ProductPricing
 * showed `net_price_plus_net_variants_price` and V2's showed `display_price.price` - and the parity
 * suite pins it (tier-strike expects $13.50 on a card holding three of them, not $40.50). The
 * quantity sits next to it on the card and the modal's CTA carries the extended total, so the
 * customer has both numbers without either surface changing meaning.
 *
 * The unit comes from the server whenever the line has been priced, because both figures are net of
 * discounts and nothing on the client can re-derive those. A line the customer just built has no
 * server pricing yet, so it falls back to the configuration's own unit price - the same
 * `unitPriceCents` the modal footer totals with, which is why the card and the modal cannot show
 * two different numbers for the same line.
 */
export function lineUnitPriceCents({ unitCents = null, viewModel = null, selection = null, quantity = 0 } = {}) {
  const units = Math.max(0, Math.trunc(Number(quantity) || 0))
  if (units <= 0) return null

  const unit = unitCents != null ? Number(unitCents) : viewModel ? unitPriceCents(viewModel, selection || {}) : null
  if (unit == null || !Number.isFinite(unit)) return null

  return unit
}

// ---------------------------------------------------------------------------
// Cart shape - both versions store variant selections identically
// ---------------------------------------------------------------------------

// The modal owns selection as `{ [groupId]: { [optionId]: quantity } }`, which is convenient for
// rendering but is not what either cart wants. Both V1's `submittableCartItem` and V2's
// `localCartItems` use the same flat `cart_item_variant_options_attributes` array, so this
// conversion is shared - V2 only diverges later, when `toPostShape` regroups it by variant.
export function selectionToCartItemVariantOptions(selection = {}) {
  const rows = []
  for (const [variantId, options] of Object.entries(selection || {})) {
    for (const [optionId, quantity] of Object.entries(options || {})) {
      const count = Number(quantity) || 0
      if (count <= 0) continue
      rows.push({
        variant_option_id: coerceId(optionId),
        variant_id: coerceId(variantId),
        quantity: count
      })
    }
  }
  return rows
}

// Object keys are always strings, but ids arrive as numbers in V2 and strings in V1. Restore the
// numeric form when it round-trips cleanly so V2 comparisons stay strict-equal safe.
function coerceId(key) {
  return /^\d+$/.test(key) ? Number(key) : key
}

// The inverse, for re-opening the modal on an existing cart line.
export function cartItemVariantOptionsToSelection(rows = []) {
  const selection = {}
  for (const row of rows || []) {
    if (!row) continue
    const variantId = row.variant_id
    const optionId = row.variant_option_id
    if (variantId == null || optionId == null) continue
    if (!selection[variantId]) selection[variantId] = {}
    selection[variantId][optionId] = Number(row.quantity) || 1
  }
  return selection
}

/**
 * What a line was configured as, ready to print on its card: `['Large', '2 x Extra bacon']`.
 *
 * Ordering the same product twice with different modifiers gives two cart lines that are identical
 * on the card - same name, same description, often the same price - so the only way to tell which
 * is which was to open each one. Group order is the merchant's, so two lines of the same product
 * always list their differences in the same places.
 */
export function selectionSummary(viewModel, selection = {}) {
  const labels = []
  for (const group of viewModel?.modifierGroups || []) {
    const chosen = selection[group.id] || {}
    for (const option of group.options || []) {
      const quantity = Number(chosen[option.id]) || 0
      if (quantity <= 0 || !option.name) continue
      labels.push(quantity > 1 ? `${quantity} x ${option.name}` : option.name)
    }
  }
  return labels
}

/**
 * The selection a package's variant preferences imply, as `{ [groupId]: { [optionId]: 1 } }`.
 *
 * A package can declare a preferred option per modifier group BY NAME - "6 oz Subscribe and Save"
 * says "protein portion" → "6oz". The backend applies that when it creates the cart item, but only
 * to a group that arrived with nothing chosen
 * (CategoryProductStoreCartItem#add_variant_preference_variants!). That was a safe assumption while
 * the card's "+" posted a line with no modifiers at all; once BOT-1108 made the customer choose
 * before adding, an explicit choice shadowed the preference every time and the merchant's package
 * quietly stopped meaning anything. Seeding the modal with it puts the preference back where the
 * customer can see it - and, unlike the backend version, override it.
 *
 * Matching is by name because that is what the backend matches on
 * (CartItemVariantInitializer → VariantOption.with_variant_name_and_variant_option_name).
 */
export function selectionFromVariantPreferences(viewModel, preferences = []) {
  const selection = {}
  if (!viewModel) return selection

  for (const preference of preferences || []) {
    const groupName = comparableName(preference && (preference.variant_name || preference.variantName))
    const optionName = comparableName(preference && (preference.variant_option_name || preference.variantOptionName))
    if (!groupName || !optionName) continue

    const group = (viewModel.modifierGroups || []).find((item) => comparableName(item.name) === groupName)
    if (!group) continue

    const option = group.options.find((item) => matchesPreferredOption(item, optionName) && !item.soldOut)
    if (!option) continue

    selection[group.id] = { [option.id]: 1 }
  }

  return selection
}

// Byte-for-byte the backend's rule (CartPricingService#matching_variant_option, and V1's
// VariantOption.with_variant_name_and_variant_option_name): an option carrying an explicit
// `autofill_preference_override` matches only that, exactly; otherwise the option's own name has
// to START WITH the preference. The prefix half matters in production - Tighten Up To Go's
// preference reads "6oz" against an option named "6oz portion" - and a stricter match here would
// pre-select nothing and hand the group back to the backend's first-option fallback.
function matchesPreferredOption(option, preferredName) {
  const override = comparableName(option.autofillPreferenceOverride)
  if (override) return override === preferredName
  return comparableName(option.name).startsWith(preferredName)
}

function comparableName(value) {
  return String(value == null ? '' : value)
    .trim()
    .toLowerCase()
}

export function groupSelectionTotal(group, selection = {}) {
  const chosen = (selection && selection[group.id]) || {}
  return Object.values(chosen).reduce((sum, quantity) => sum + (Number(quantity) || 0), 0)
}

// A required group is satisfied once it has at least `min` selections (at least one when the
// backend only says `required`), and a quantity group only while it stays within its limit. This is
// the client-side half of an enforcement that is otherwise server-only for choose_one/choose_many,
// so it must agree with the backend rather than replace it.
export function unsatisfiedModifierGroups(viewModel, selection = {}) {
  return (viewModel.modifierGroups || []).filter((group) => {
    const count = groupSelectionTotal(group, selection)
    if (group.mode === QUANTITY && group.limit != null && count > group.limit) return true
    return group.required && count < Math.max(group.min, 1)
  })
}
